/**
 * Haptic Feedback Utility Hook
 * 
 * Provides consistent haptic feedback patterns across mobile components.
 * Uses the Vibration API when available, gracefully degrades on unsupported devices.
 * 
 * Usage:
 *   const { trigger } = useHaptics();
 *   trigger('light');  // Light tap feedback
 *   trigger('medium'); // Medium feedback for confirmations
 *   trigger('heavy');  // Strong feedback for important actions
 *   trigger('success'); // Success pattern
 */

/**
 * Haptic feedback patterns (vibration durations in ms)
 */
export const HAPTIC_PATTERNS = {
  light: [10],
  medium: [20],
  heavy: [30, 10, 30],
  success: [50],
  error: [100, 50, 100],
};

/**
 * Trigger haptic feedback with a named pattern
 * @param {keyof typeof HAPTIC_PATTERNS} pattern - Pattern name
 */
export function triggerHaptic(pattern = 'light') {
  if (typeof navigator !== 'undefined' && navigator.vibrate) {
    const vibrationPattern = HAPTIC_PATTERNS[pattern] || HAPTIC_PATTERNS.light;
    navigator.vibrate(vibrationPattern);
  }
}

/**
 * Hook for haptic feedback in React components
 * @returns {{ trigger: (pattern?: string) => void }}
 */
export function useHaptics() {
  return {
    trigger: triggerHaptic,
  };
}

export default useHaptics;
